/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {FieldLabel} from '../components/forms/FieldLabel';
import {FileBrowseButton} from '../editors/controls/FileBrowseButton';
import {NotificationGeneralAlertIcon} from '../icons/NotificationGeneralAlertIcon';
import {dlsAlt, dlsColor} from '../theme/theme';
import {FILE_TYPES} from '../utils/ComponentUtils';
import {ImportParamsType} from './BulkUploadDialog';

// defines the prop definitions for bulk upload importer ...
export interface BulkUploadImporterType {
  entityLabel: string;
  entity: string;
  uploadTitle: string;
  onImport: (file: FileList) => void;
  importParams: ImportParamsType;
  size?: string;
  id: string;
  disabled?: boolean;
  omitInstructions?: boolean;
  isFormField?: boolean;
  isRequired?: boolean;
}

const getDisabledColor = (disabled: boolean) => disabled ? dlsColor('grey5') : dlsColor('aaBlue');

// defines the styles used by bulk upload importer ...
export const useStyles = makeStyles(theme => ({
  uploadContainer : {
    display        : 'flex',
    justifyContent : 'center',
    flexDirection  : 'column',
    alignItems     : 'center',
    marginTop      : ({isFormField}: BulkUploadImporterType) => !isFormField && '30px',
    padding        : '40px 15px',
    background     : dlsColor('white'),
    border         : ({
      disabled,
      isFormField
    }: BulkUploadImporterType) => !(disabled || isFormField) && `1px dashed ${getDisabledColor(disabled)}`,
    boxSizing : 'border-box'
  },
  icon : {
    display       : 'inline-block',
    verticalAlign : 'middle',
    paddingTop    : 4,
    marginRight   : 24
  },
  message : {
    ...dlsAlt('typography', 'label2'),
    color         : ({disabled}: BulkUploadImporterType) => getDisabledColor(disabled),
    textTransform : 'upperCase',
    fontWeight    : 'bold',
    width         : '132px',
    margin        : '0 auto',
    textAlign     : 'center'
  },
  results : {
    ...dlsAlt('components', 'uploadResults', 'container'),
    display    : 'flex',
    alignItems : 'center',
    padding    : '0 10px',
    '& > p'    : {
      margin : '10px 0'
    }
  },
  uploadTitle : {
    textTransform : 'uppercase'
  },
  disabled : {
    pointerEvents : 'none'
  }
}));

/**
 * Wrapper component for Bulk Upload Importer.
 *
 * ```typescript
 *
 * import {BulkUploadImporter} from '@ion/dls/src/imports';
 *
 * <BulkUploadImporter
 *   entity={'asset'}
 *   uploadTitle='Upload Assets'
 * >
 * </BulkUploadImporter>
 * ```
 *
 * @param entityLabel The current CTCEntity label under bulk import
 * @param id The upload container id
 * @param uploadTitle The upload container title
 * @param onImport callback to be executed when user initiates import post successsful upload
 * @param importParams The params to initiate import
 * @param size The size of the file which was uploaded
 * @param omitInstructions Whether to include the instructions
 */
export const BulkUploadImporter = ({
  entityLabel,
  entity,
  id,
  uploadTitle,
  onImport,
  importParams,
  size = '',
  disabled = false,
  omitInstructions = false,
  isFormField = false,
  isRequired = false
}: BulkUploadImporterType) => {
  const classes = useStyles({disabled, isFormField});
  const {
    fileName,
    fileSize,
    isComplete,
    isFailure,
    items
  } = importParams || {};

  return (
    <div>
      {isFormField ? (
        <FieldLabel label={uploadTitle} isRequired={isRequired} />
      ): (
        <h3 className={classes.uploadTitle}>{uploadTitle}</h3>
      )}
      {!omitInstructions && (
        <p>{translate('common:editors.import.instructions',
          {entity: entityLabel?.toLowerCase()})}
        </p>
      )}
      {isFailure && (
        <div className={classes.results}>
          <p>
            <div className={classes.icon}>
              <NotificationGeneralAlertIcon color={dlsColor('white')} />
            </div>
            {translate('common:editors.import.importErrorMsg')}
          </p>
        </div>
      )}
      <div id={id} className={classes.uploadContainer}>
        <div className={disabled ? classes.disabled : ''}>
          <FileBrowseButton onImport={onImport}
            disabled={disabled}
            entity={entity}
            fileTypes={FILE_TYPES} />
          <p className={classes.message}>
            {translate('common:editors.import.dragAndDropMsg')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadImporter;
